/**
 * Types and utilities for dealing with email domain verification
 */

import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * Status of DKIM verification for a domain
 */
export enum DkimStatus {
  PENDING = "pending",
  VERIFIED = "verified",
  FAILED = "failed",
  NOT_STARTED = "not_started",
}

/**
 * Status of Mail From domain verification
 */
export enum MailFromDomainStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  NOT_STARTED = "not_started",
}

/**
 * Result of an independent DNS lookup for a single required record.
 * Unlike DkimStatus / MailFromDomainStatus (which come from AWS SES),
 * these are populated by Redo's own DNS resolution in dns-checker.ts.
 */
export enum DnsRecordCheckStatus {
  /** The record resolves and contains the required tokens. */
  OK = "ok",
  /** No record of the expected kind resolves at the expected hostname. */
  MISSING = "missing",
  /**
   * A record resolves but is misconfigured — e.g. SPF is present but omits
   * `include:amazonses.com`, DKIM TXT exists but doesn't match the team's key,
   * MX points somewhere other than an `*.amazonses.com` host.
   */
  MISCONFIGURED = "misconfigured",
  /** DNS query itself failed (timeout, SERVFAIL, etc.) — status unknown. */
  ERROR = "error",
  /** This record type does not apply to the current configuration (e.g. no MailFrom set). */
  NOT_APPLICABLE = "not_applicable",
}

/** Identifier for each DNS record type Redo checks. */
export enum DnsRecordKind {
  DKIM = "dkim",
  SPF = "spf",
  DMARC = "dmarc",
  MAIL_FROM_MX = "mail_from_mx",
  MAIL_FROM_SPF = "mail_from_spf",
}

export interface DnsRecordCheck {
  status: DnsRecordCheckStatus;
  /** Last time we attempted to resolve this record. */
  lastCheckedAt?: Date | null;
  /** Last time `status` changed value — used to detect regressions. */
  lastChangedAt?: Date | null;
  /**
   * Observed DNS value(s), truncated for storage. Helpful for support/debugging.
   * For TXT records: concatenated record strings. For MX: the targets.
   */
  observed?: string | null;
}

/**
 * Per-record independent DNS check state. Each field is optional (we may not
 * have checked yet) but the *set of keys* is closed: adding a new
 * `DnsRecordKind` will break type checking here until this type is extended,
 * which in turn forces the mongoose schema + checker to be updated.
 */
export type DnsRecordStatusMap = {
  [K in DnsRecordKind]?: DnsRecordCheck | null;
};

export const teamEmailAddressSchema = z.object({
  id: z.string(),
  localPart: z.string(),
  domain: z.string(),
  displayName: z.string(),
  isMailbox: z.boolean().nullish(),
  mailboxVerifiedAt: z.date().nullish(),
  _id: zExt.objectId(),
});
export type TeamEmailAddress = z.infer<typeof teamEmailAddressSchema>;

export interface DomainWarmingSettings {
  from: {
    domain: string;
    configurationSets?: { transactional: string; marketing: string } | null;
  };
  to: {
    domain: string;
    configurationSets?: { transactional: string; marketing: string } | null;
  };
  currentTier: DomainWarmingEntry;
  history: DomainWarmingEntry[];
  startedAt: Date;
  finishedAt?: Date | null;
}

export interface DomainWarmingEntry {
  tierIndex: number;
  enteredAt: Date;
}

export const WARMING_DOMAINS = [
  "shared1.getredomail.com",
  "shared2.getredomail.com",
  "shared3.getredomail.com",
  "shared4.getredomail.com",
] as const;

export const TRANSACTIONAL_EMAIL_DOMAIN = "notify.getredomail.com";
export const TRANSACTIONAL_EMAIL_CONFIGURATION_SET =
  "notify_getredomail_com-txnl-conf-set";
export const MARKETING_EMAIL_DOMAIN = "shared.getredomail.com";
export const MARKETING_EMAIL_CONFIGURATION_SET =
  "shared_getredomail_com-mktg-conf-set";
export const WARMING_EMAIL_CONFIGURATION_SET =
  "warming_getredomail_com-mktg-conf-set";
